import {
  ChangeValuesAndUpdate,
  ComputeOffsetBasedOnDrag,
  ComputeOffset,
  Dec,
  defaultStepSize,
  Inc,
  SliderOffset,
} from "./slider.types";
import offset from "../utils/dom/offset";

// curry function to match the arguments in the dom util
export const getOffset = (node: Element): SliderOffset =>
  offset(node, false) as SliderOffset;

// Get slider width from ref
export const getSliderWidth = (node: Element): number => {
  if (node) {
    const sliderObj = getOffset(node);
    if (typeof sliderObj === "object") {
      return sliderObj.width;
    }
  }
  return 1000;
};

// Get number of digits in decimal places from string arg
export const getNumberOfDecimalPlaces = (step: string): number =>
  step.toString().split(".")[1]?.length || 0;

// Convert number into string with a % symbol
export const toStringPercentage = (value: number): string => {
  return (Math.abs(value) * 100).toString() + "%";
};

/**
 * Calculate the offset corresponding to the value of the slider.
 * This offset will be used for button position and track width
 */
export const computeOffset = ({
  minValue,
  maxValue,
  value,
  floorValue = 0,
  ceilValue = 0,
  stepValue,
  sliderWidth,
  buttonWidth,
}: ComputeOffset): {
  buttonOffset: string;
  trackOffset: number;
} => {
  let offset = 0;
  if (value <= minValue) {
    offset = 0;
  }

  if (value > minValue && value <= maxValue) {
    const differenceBetweenMinMax = maxValue - minValue;
    let newValue = value;
    if (value >= ceilValue) {
      newValue = ceilValue;
    } else if (value <= floorValue) {
      newValue = floorValue + stepValue;
    }
    offset = (newValue - minValue) / differenceBetweenMinMax;
  }

  const usableTrackWidth = sliderWidth - buttonWidth;
  const buttonOffset =
    (usableTrackWidth * offset + buttonWidth / 2) / sliderWidth;

  return {
    buttonOffset: toStringPercentage(buttonOffset),
    trackOffset: Math.round(Math.abs(offset) * 100),
  };
};

/**
 * Decrement the value of slider by one step based on keyboard events
 */
export const decrementValue = ({
  value,
  minValue,
  step = defaultStepSize,
  decimalsLength,
}: Dec): number => {
  // Check the min condition before decrementing
  if (value - step <= minValue) {
    return minValue;
  }

  return Number((value - step).toFixed(decimalsLength));
};

/**
 * Increment the value of slider by one step based on keyboard events
 */
export const incrementValue = ({
  value,
  maxValue,
  step = defaultStepSize,
  decimalsLength,
}: Inc): number => {
  // Check the max condition before incrementing
  if (value + step >= maxValue) {
    return maxValue;
  }
  return Number((value + step).toFixed(decimalsLength));
};

/**
 * Calculate the value of the slider based on width, position of button
 */
export const computeValueBasedOnDragPosition = ({
  dragPosition,
  sliderWidth,
  step,
  minValue,
  maxValue,
  decimalsInStep,
}: ComputeOffsetBasedOnDrag): number => {
  const percentageDrag = dragPosition / sliderWidth;

  if (percentageDrag <= 0) {
    return minValue;
  }

  if (!Number.isInteger(step)) {
    let value = percentageDrag * (maxValue - minValue) + minValue;
    value = Number(value.toFixed(decimalsInStep));
    const newValue = Number(
      (Math.round(value / step) * step).toFixed(decimalsInStep)
    );
    return newValue > maxValue ? maxValue : newValue;
  }

  const relativeValueOfDrag =
    Math.ceil(percentageDrag * (maxValue - minValue)) + minValue;
  const adjustedValueBasedOnStep = Math.ceil(relativeValueOfDrag / step) * step;

  return adjustedValueBasedOnStep > maxValue
    ? maxValue
    : adjustedValueBasedOnStep;
};

export const computeTrackWidth = (
  startButtonOffset: string,
  endButtonOffset: string
): string => {
  const width =
    Number(endButtonOffset.replace("%", "")) -
    Number(startButtonOffset.replace("%", ""));
  return width + "%";
};

interface UpdateValuesOnBlur {
  updaterFn: (value: number) => void;
  disabled?: boolean;
  maxValue: number;
  minValue: number;
  valueFromInput: string;
}

// Triggered when user is typing into the input and presses enter / blur from input
export const updateValuesOnBlur = ({
  updaterFn,
  disabled = false,
  maxValue,
  minValue,
  valueFromInput,
}: UpdateValuesOnBlur): void => {
  const inputValue = Number(valueFromInput);
  if (disabled) {
    return;
  }
  if (inputValue >= maxValue) {
    updaterFn(maxValue);
    return;
  }
  if (inputValue <= minValue) {
    updaterFn(minValue);
    return;
  }
  updaterFn(inputValue);
};

export const changeValuesAndUpdate = ({
  key,
  upDaterFn,
  currentMinValue,
  currentValue,
  step,
  currentMaxValue,
  decimalsInStep,
  isButton,
}: ChangeValuesAndUpdate): void => {
  if (key === "ArrowUp" || (isButton && key === "ArrowRight")) {
    upDaterFn(
      incrementValue({
        value: currentValue,
        step,
        maxValue: currentMaxValue,
        decimalsLength: decimalsInStep,
      })
    );
  }

  if (key === "ArrowDown" || (isButton && key === "ArrowLeft")) {
    upDaterFn(
      decrementValue({
        minValue: currentMinValue,
        value: currentValue,
        step,
        decimalsLength: decimalsInStep,
      })
    );
  }

  if (key === "Enter") {
    updateValuesOnBlur({
      updaterFn: upDaterFn,
      minValue: currentMinValue,
      maxValue: currentMaxValue,
      valueFromInput: currentValue.toString(),
    });
  }
};

export const getDecimalPlacesForFormatNumber = (
  numberFromSlider: number,
  numberRange: number,
  numberOfDecimalPlacesFromStep: number
): number => {
  const isNotPerfectMultiple = numberFromSlider % numberRange !== 0;
  return isNotPerfectMultiple
    ? numberOfDecimalPlacesFromStep || 1
    : numberOfDecimalPlacesFromStep;
};

export const formatNumber = (
  inputNum: number,
  numberOfDecimalPlaces: number
): string | void => {
  const prefix = inputNum >= 0 ? "" : "-";
  const n = Math.abs(inputNum);
  let outputString = "";
  if (n < 1e3) {
    outputString = `${prefix}${n.toFixed(
      getDecimalPlacesForFormatNumber(n, 1, numberOfDecimalPlaces)
    )}`;
  } else if (n >= 1e3 && n < 1e6) {
    outputString =
      prefix +
      (n / 1e3).toFixed(
        getDecimalPlacesForFormatNumber(n, 1e3, numberOfDecimalPlaces)
      ) +
      "k";
  } else if (n >= 1e6 && n < 1e9) {
    outputString =
      prefix +
      (n / 1e6).toFixed(
        getDecimalPlacesForFormatNumber(n, 1e6, numberOfDecimalPlaces)
      ) +
      "m";
  } else if (n >= 1e9 && n < 1e12) {
    outputString =
      prefix +
      (n / 1e9).toFixed(
        getDecimalPlacesForFormatNumber(n, 1e9, numberOfDecimalPlaces)
      ) +
      "b";
  } else if (n >= 1e12) {
    outputString =
      prefix +
      (n / 1e12).toFixed(
        getDecimalPlacesForFormatNumber(n, 1e12, numberOfDecimalPlaces)
      ) +
      "t";
  }
  return outputString;
};
